/**
 * Runtime validation for stored `StoreThemeJson`. Used in three places:
 *
 * 1. Backend `seller/store-theme` service before persisting `draft_*`.
 * 2. Backend `seller/store-theme` service before publishing (defense in
 *    depth — the editor's pre-publish gate runs the same checks).
 * 3. CI snapshot/contract tests.
 *
 * Validation here is structural: does the JSON match the schema shape, and can
 * the storefront RENDER what it describes. It is not a review of how complete
 * the seller's content is — see `ADVISORY_CODES` — and it is not SEO validation,
 * which lives in `./seo/validateSeo.ts` and never blocks anything.
 *
 * The governing rule (see the healing pass in `validateThemeJson`): prefer
 * REPAIR to rejection. A theme carrying something unrenderable comes back with
 * that thing removed and a warning naming it, not as a refusal. A seller's save
 * is only ever refused for a payload we can neither render nor repair.
 */
import { z } from "zod";
import { CURRENT_SCHEMA_VERSION } from "./version";
import { MAX_CUSTOM_PAGES } from "./types";
import { isSectionAvailableOnTemplate, requiredLockedSectionTypes, sectionRegistry, } from "./registry";
import { getProductCardSchema } from "./product-cards";
/**
 * Codes that describe CONTENT QUALITY rather than renderability — reported so
 * the editor can hint, never blocking a save or a publish.
 *
 * The distinction this whole file turns on: a theme is rejected only when the
 * storefront could not RENDER it (an unknown section type, a missing header, a
 * `javascript:` URL). Everything below renders perfectly well; it is just not
 * what we would have written. Blocking on it costs the seller their edit and
 * buys nothing:
 *
 * - `required` / `bilingual_empty` — an unfilled field renders as nothing, and
 *   a section with nothing in it is DROPPED from the live storefront outright
 *   (`isSettingsSectionEmpty` in the buyer). The editor keeps it visible as a
 *   placeholder so the seller can come back and fill it. Bouncing the save
 *   meant a seller could not park a half-built section and finish it later —
 *   the single most common thing anyone does in an editor.
 * - `too_short` — an 8-character minimum on a hero headline is a style opinion.
 *   "SALE" renders fine.
 * - `too_long` — the bound is a layout preference. The storefront wraps or
 *   clips text past it, which is a smaller problem than a seller unable to save
 *   because of a headline a seeded default or a paste put there.
 * - `too_many` / `too_many_sections` / `too_many_pages` — extra items, repeated
 *   sections and extra pages all render; the caps are editorial guidance about
 *   what looks good, and the editor already enforces them where it matters by
 *   disabling the add button.
 * - `section_not_allowed_on_template` / `section_not_allowed_on_custom_page` /
 *   `locked_section_on_custom_page` — `availableOnTemplates` decides what the
 *   ADD-SECTION MENU offers, not what the buyer can draw: the section type is
 *   registered and its renderer works. Note these are deliberately advisory
 *   rather than healed. Stripping a misplaced section would mean that one wrong
 *   `storeKind` — a restaurant theme validated as a shop — silently guts the
 *   whole theme on save, which is a far worse outcome than the misplacement.
 *
 * Everything NOT listed here still blocks — but note how little that leaves in
 * practice, because the healing pass in `validateThemeJson` REMOVES the
 * unrenderable rather than rejecting the theme that contains it. What survives
 * both is only what we can neither render nor repair: a payload that isn't a
 * theme at all, and a `schemaVersion` from a future build.
 *
 * Adding a code to this set is a product decision: it must be true that the
 * storefront renders correctly with the offending value left in place. If it
 * does NOT render, the answer is a healer, not an entry here.
 */
const ADVISORY_CODES = new Set([
    "required",
    "bilingual_empty",
    "too_short",
    "too_long",
    "too_many",
    "too_many_sections",
    "too_many_pages",
    "section_not_allowed_on_template",
    "section_not_allowed_on_custom_page",
    "locked_section_on_custom_page",
    // A link the storefront cannot follow but CAN render (`href="exa"` is a
    // harmless relative link). Kept as typed: sellers type URLs in pieces and
    // an autosave lands between every keystroke — dropping the value would erase
    // the link under their fingers. Bare domains are repaired instead (see
    // `repairBareDomain`); dangerous schemes stay `invalid_url` and are dropped.
    "unresolved_url",
]);
/**
 * `example.com`, `www.shop.eg/sale`, `wa.me/2010…` — what sellers actually
 * paste into a link field. Not a URL the storefront can follow, but one it
 * can repair unambiguously by prefixing `https://`. Relative paths, anchors
 * and anything carrying its own scheme never match.
 */
const BARE_DOMAIN_RE = /^(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:[/?#][^\s]*)?$/i;
function repairBareDomain(value) {
    if (typeof value !== "string")
        return null;
    const trimmed = value.trim();
    if (!BARE_DOMAIN_RE.test(trimmed))
        return null;
    return `https://${trimmed}`;
}
/**
 * Split a collected error list into the ones that genuinely block and the ones
 * that are only worth hinting about. Advisory entries keep their code and path
 * so the editor's existing per-code copy still names the field.
 */
function partitionBySeverity(errors) {
    const blocking = [];
    const advisory = [];
    for (const err of errors) {
        (ADVISORY_CODES.has(err.code) ? advisory : blocking).push(err);
    }
    return { blocking, advisory };
}
// ---------------------------------------------------------------------------
// Primitive zod schemas
// ---------------------------------------------------------------------------
const localeStringSchema = z.object({
    ar: z.string().nullable(),
    en: z.string().nullable(),
});
/**
 * Image value: `url` is the only hard requirement. `objectKey` is present
 * for images uploaded through the editor's media library, but externally
 * hosted images (defaults seeded for new stories blocks, sellers pasting
 * hosted URLs, etc.) only carry `url` and would otherwise fail validation
 * even though they render correctly on the storefront.
 */
const imageValueSchema = z.object({
    objectKey: z.string().min(1).optional(),
    url: z.string().url(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    altAr: z.string().optional(),
    altEn: z.string().optional(),
    focalX: z.number().min(0).max(100).optional(),
    focalY: z.number().min(0).max(100).optional(),
    fit: z.enum(["cover", "contain"]).optional(),
});
const buttonVariantStyleSchema = z.object({
    background: z.string().optional(),
    text: z.string().optional(),
    radius: z.string().optional(),
    size: z.enum(["sm", "md", "lg"]).optional(),
    underline: z.boolean().optional(),
});
/** A product-card selection: card id + its card-local settings bag. Mirrors
 *  `ProductCardInstance` in types.ts (kept zod-free); the assertion below
 *  guards the two against drift. */
const productCardInstanceSchema = z.object({
    type: z.string(),
    settings: z.record(z.string(), z.unknown()),
});
const themeTokensSchema = z.object({
    colors: z.object({
        primary: z.string(),
        accent: z.string(),
        surface: z.string(),
        ink: z.string(),
        surfaceSubtle: z.string().optional(),
        surfaceDark: z.string().optional(),
        pageGround: z.string().optional(),
        footerSurface: z.string().optional(),
        footerInk: z.string().optional(),
        footerHeading: z.string().optional(),
        headerSurface: z.string().optional(),
        headerInk: z.string().optional(),
    }),
    typography: z.object({
        heading: z.string(),
        body: z.string(),
        headingEn: z.string().optional(),
        headingAr: z.string().optional(),
        bodyEn: z.string().optional(),
        bodyAr: z.string().optional(),
        displayEn: z.string().optional(),
        displayAr: z.string().optional(),
        monoEn: z.string().optional(),
    }),
    radius: z.string(),
    logoTreatment: z.enum(["square", "circle", "none", "wordmark"]),
    bandRadius: z.string().optional(),
    heroHeight: z.string().optional(),
    bandHeight: z.string().optional(),
    pageGutter: z.string().optional(),
    pageGutterMobile: z.string().optional(),
    heroBleed: z.boolean().optional(),
    headingRole: z.enum(["ink", "primary", "accent"]).optional(),
    // Structured product-card selection (successor to the flat cluster below).
    productCard: productCardInstanceSchema.optional(),
    productCardStyle: z
        .enum([
        "grid",
        "clean",
        "overlay",
        "compact",
        "card",
        "simple",
        "soft-tile",
        "editorial",
        "centered-accent",
        "rounded-shadow",
        "list-row",
        "bordered",
        "framed",
        "bold-tile",
        "catalog",
        "showcase",
        "gamer",
    ])
        .optional(),
    productCardRadius: z.string().optional(),
    productCardRadiusInherit: z.boolean().optional(),
    productCardShadow: z.enum(["none", "sm", "md", "lg"]).optional(),
    productCardImageFit: z.enum(["cover", "contain"]).optional(),
    productCardImageBg: z.string().optional(),
    productCardShowColors: z.boolean().optional(),
    productCardShowAddButton: z.boolean().optional(),
    productCardAddButtonStyle: z.enum(["outline", "solid"]).optional(),
    productCardAddButtonRadius: z.string().optional(),
    productCardAddButtonLabel: localeStringSchema.optional(),
    buttonStyle: z.enum(["solid", "outline", "ghost", "link"]).optional(),
    buttonVariants: z
        .object({
        solid: buttonVariantStyleSchema.optional(),
        outline: buttonVariantStyleSchema.optional(),
        ghost: buttonVariantStyleSchema.optional(),
        link: buttonVariantStyleSchema.optional(),
    })
        .optional(),
    buttonBackground: z.string().optional(),
    buttonTextColor: z.string().optional(),
    buttonRadius: z.string().optional(),
    buttonSize: z.enum(["sm", "md", "lg"]).optional(),
    // Theme-level scroll-reveal driver (see ThemeTokens). Absent ⇒ off, so
    // existing stores stay static.
    sectionReveal: z.boolean().optional(),
    sectionRevealStyle: z.enum(["uniform", "cycle"]).optional(),
    sectionRevealSpeed: z.number().optional(),
    // RESTAURANT stores only — which skin draws the storefront. A free string
    // rather than an enum of the six: the buyer falls back when it cannot
    // resolve one, so an unknown value must VALIDATE and degrade, not reject a
    // whole theme and take the menu down with it.
    layout: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Theme token allow-lists — HEALED on write, never rejected
//
// Theme tokens end up inside a <style> element on every storefront page
// (apps/buyer `StorefrontThemeStyle.tsx`). The renderer passes each one through
// a whitelist sanitizer, and that stays the real protection for rows written
// before these rules existed and for writes that skip this validator. The
// patterns below are the SAME allow-lists, applied on write so a value the
// renderer would discard is never stored.
//
// Heal-don't-reject (see `validateAndHealField`): a token the renderer cannot
// use gets exactly what the renderer would have shown instead — values are
// TRIMMED first (the renderer trims); an invalid OPTIONAL token is dropped (the
// renderer's "absent" path); an invalid REQUIRED token is replaced with the
// renderer's fallback; a font name has disallowed characters stripped, as
// `sanitizeFontName` does, and an empty result gets the face the renderer
// would show (see `healFont`). Every change is reported as a `healed_theme_token`
// warning, which the backend's `logHealing` logs by its `healed_` prefix. The
// rejected value itself is never echoed into the message (it may be a payload).
// Keep these in step with the renderer — widening one without the other either
// drops a value that renders, or stores a value that silently falls back.
// ---------------------------------------------------------------------------
/** `sanitizeColor` / `sanitizeColorOptional`: `#hex`, `rgb()`/`hsl()`/`oklch()`/
 *  `oklab()` with numeric arguments only, or a bare named colour. */
const CSS_COLOR_RE = /^(?:#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|(?:rgb|rgba|hsl|hsla|oklch|oklab)\([\d\s.,%/+-]+\)|[A-Za-z]+)$/;
/** `sanitizeLength`: `0` or a number with rem/em/px/vw/%/vh/svh/dvh. */
const CSS_LENGTH_RE = /^(?:0|\d+(?:\.\d+)?(?:rem|em|px|vw|%|vh|svh|dvh))$/;
/** `sanitizeRadius`: `0` or a number with rem/em/px. */
const CSS_RADIUS_RE = /^(?:0|\d+(?:\.\d+)?(?:rem|em|px))$/;
/** `sanitizeFontName` strips everything but letters, digits, spaces, hyphens. */
const FONT_NAME_DISALLOWED_RE = /[^A-Za-z0-9 -]/g;
/** `sanitizeColor`'s fallback for a required colour. */
const FALLBACK_COLOR = "#1D9E75";
/** `sanitizeFontName`'s fallback for a NON-EMPTY font name that strips to nothing. */
const FALLBACK_FONT = "Cairo";
/** What `t.headingEn || t.heading || "Inter"` (and the body twin) shows for an EMPTY required font. */
const FALLBACK_FONT_UNSET = "Inter";
/** `sanitizeRadius`'s fallback for the required theme radius. */
const FALLBACK_RADIUS = "0.5rem";
const TOKEN_KIND_DESCRIPTION = {
    color: "not a CSS colour (#hex, rgb()/hsl()/oklch() or a colour name)",
    colorOrEmpty: "not a CSS colour (#hex, rgb()/hsl()/oklch() or a colour name)",
    length: "not 0 or a number with rem, em, px, vw, %, vh, svh or dvh",
    radius: "not 0 or a number with rem, em or px",
};
/**
 * Heal one token in place. `fallback` is set for REQUIRED tokens (the value
 * the renderer substitutes); without it an unusable value is deleted.
 */
function healToken(bag, key, kind, path, warnings, fallback) {
    if (!bag || !(key in bag))
        return;
    const raw = bag[key];
    if (typeof raw !== "string")
        return; // the shape schema owns non-strings
    const fullPath = `${path}.${key}`;
    const trimmed = raw.trim();
    const re = kind === "length" ? CSS_LENGTH_RE : kind === "radius" ? CSS_RADIUS_RE : CSS_COLOR_RE;
    const healed = (kind === "colorOrEmpty" && trimmed === "") || re.test(trimmed) ? trimmed : null;
    if (healed === raw)
        return;
    if (healed !== null) {
        bag[key] = healed;
        warnings.push({
            path: fullPath,
            code: "healed_theme_token",
            message: `${fullPath}: surrounding whitespace removed`,
        });
        return;
    }
    if (fallback !== undefined) {
        bag[key] = fallback;
        warnings.push({
            path: fullPath,
            code: "healed_theme_token",
            message: `${fullPath}: ${TOKEN_KIND_DESCRIPTION[kind]} — replaced with the storefront's fallback ${fallback}`,
        });
        return;
    }
    delete bag[key];
    warnings.push({
        path: fullPath,
        code: "healed_theme_token",
        message: `${fullPath}: ${TOKEN_KIND_DESCRIPTION[kind]} — value dropped, the storefront falls back to its default`,
    });
}
/** Font tokens the renderer reads as `(t.x ?? "").trim()`, so whitespace alone means "unset". */
const TRIM_READ_FONTS = new Set(["displayEn", "monoEn"]);
/**
 * Heal one font name to exactly what `StorefrontThemeStyle` would show for it:
 * - disallowed characters are stripped and the rest trimmed, as `sanitizeFontName` does;
 * - `""` on an OPTIONAL font is left alone — every renderer read (`t.x || …`,
 *   `(t.x ?? "").trim()`) treats it as unset, so it renders identically;
 * - `""` on a REQUIRED font (`heading`/`body`) becomes `Inter`, which is what
 *   `t.headingEn || t.heading || "Inter"` shows for it;
 * - a non-empty value that strips to nothing becomes `Cairo`, `sanitizeFontName`'s
 *   fallback — except whitespace alone on `displayEn`/`monoEn`, which the renderer
 *   reads as unset, so it is dropped.
 */
function healFont(bag, key, required, warnings) {
    if (!bag || !(key in bag))
        return;
    const raw = bag[key];
    if (typeof raw !== "string")
        return; // the shape schema owns non-strings
    const fullPath = `settings.typography.${key}`;
    const replace = (value, why) => {
        bag[key] = value;
        warnings.push({ path: fullPath, code: "healed_theme_token", message: `${fullPath}: ${why}` });
    };
    if (raw === "") {
        if (required)
            replace(FALLBACK_FONT_UNSET, `empty — replaced with the storefront's fallback ${FALLBACK_FONT_UNSET}`);
        return;
    }
    const stripped = raw.replace(FONT_NAME_DISALLOWED_RE, "").trim();
    if (stripped === raw)
        return;
    if (stripped.length > 0) {
        replace(stripped, stripped === raw.trim()
            ? "surrounding whitespace removed"
            : "font names may contain only letters, digits, spaces and hyphens — disallowed characters removed");
        return;
    }
    if (!required && TRIM_READ_FONTS.has(key) && raw.trim() === "") {
        delete bag[key];
        warnings.push({
            path: fullPath,
            code: "healed_theme_token",
            message: `${fullPath}: only whitespace — value dropped, the storefront treats it as unset`,
        });
        return;
    }
    replace(FALLBACK_FONT, `font names may contain only letters, digits, spaces and hyphens — replaced with the storefront's fallback ${FALLBACK_FONT}`);
}
const REQUIRED_COLORS = ["primary", "accent", "surface", "ink"];
const OPTIONAL_COLORS = [
    "surfaceSubtle",
    "surfaceDark",
    "pageGround",
    "footerSurface",
    "footerInk",
    "footerHeading",
    "headerSurface",
    "headerInk",
];
const REQUIRED_FONTS = ["heading", "body"];
const OPTIONAL_FONTS = ["headingEn", "headingAr", "bodyEn", "bodyAr", "displayEn", "displayAr", "monoEn"];
const OPTIONAL_LENGTHS = ["bandRadius", "heroHeight", "bandHeight", "pageGutter", "pageGutterMobile"];
const OPTIONAL_RADII = ["productCardRadius", "productCardAddButtonRadius", "buttonRadius"];
const OPTIONAL_COLORS_OR_EMPTY = ["productCardImageBg", "buttonBackground", "buttonTextColor"];
const BUTTON_VARIANTS = ["solid", "outline", "ghost", "link"];
/** Bring every theme token inside the renderer's allow-lists, in place. */
function healThemeTokens(settings, warnings) {
    const s = settings;
    const colors = settings.colors;
    const typography = settings.typography;
    for (const k of REQUIRED_COLORS)
        healToken(colors, k, "color", "settings.colors", warnings, FALLBACK_COLOR);
    // An optional colour that is "" renders exactly like an absent one
    // (`sanitizeColorOptional` returns ""), so it is kept as written.
    for (const k of OPTIONAL_COLORS)
        healToken(colors, k, "colorOrEmpty", "settings.colors", warnings);
    for (const k of REQUIRED_FONTS)
        healFont(typography, k, true, warnings);
    for (const k of OPTIONAL_FONTS)
        healFont(typography, k, false, warnings);
    healToken(s, "radius", "radius", "settings", warnings, FALLBACK_RADIUS);
    for (const k of OPTIONAL_LENGTHS)
        healToken(s, k, "length", "settings", warnings);
    for (const k of OPTIONAL_RADII)
        healToken(s, k, "radius", "settings", warnings);
    for (const k of OPTIONAL_COLORS_OR_EMPTY)
        healToken(s, k, "colorOrEmpty", "settings", warnings);
    const variants = settings.buttonVariants;
    if (variants) {
        for (const name of BUTTON_VARIANTS) {
            const v = variants[name];
            const path = `settings.buttonVariants.${name}`;
            healToken(v, "background", "colorOrEmpty", path, warnings);
            healToken(v, "text", "colorOrEmpty", path, warnings);
            healToken(v, "radius", "radius", path, warnings);
        }
    }
}
/** Instantiating with anything but `true` is a compile error. */
function assertExact() {
    /* type-level only — never invoked at runtime */
}
assertExact();
assertExact();
assertExact();
assertExact();
/**
 * Run one field through {@link validateFieldValue}, and — when the value turns
 * out to be unusable — DELETE it from the settings bag instead of rejecting the
 * whole theme.
 *
 * This is the field-level half of the heal-don't-reject contract. A value the
 * renderer cannot use (a select option that no longer exists, a colour that
 * isn't a string, a range past its bounds, a malformed image object) has
 * exactly one sensible fate: drop it, and let the schema's own default take
 * over. That is precisely what the storefront would show anyway. The previous
 * behaviour — refusing to store the entire theme until someone fixed a value
 * the editor no longer has a control for — was strictly worse for everyone:
 * the seller could not save, and the bad value stayed exactly where it was.
 *
 * Errors become warnings so the outcome is still visible in logs, keeping their
 * original `code` so the editor's existing per-code copy still names the field.
 * `required` is left alone: there is no value to delete, and it is advisory.
 */
function validateAndHealField(field, bag, basePath) {
    // Repair before judging: a bare domain becomes an https URL and is then a
    // perfectly valid value — the seller keeps the link, the storefront gets one
    // it can follow.
    if (field.type === "url" && bag) {
        const repaired = repairBareDomain(bag[field.id]);
        if (repaired !== null) {
            bag[field.id] = repaired;
            const out = validateFieldValue(field, repaired, basePath);
            return {
                ...out,
                warnings: [
                    ...out.warnings,
                    {
                        path: `${basePath}.${field.id}`,
                        code: "healed_url_scheme",
                        message: `Link repaired to ${repaired}`,
                    },
                ],
            };
        }
    }
    const out = validateFieldValue(field, bag?.[field.id], basePath);
    if (out.errors.length === 0)
        return out;
    // Advisory codes describe content we can render perfectly well — a headline
    // past its suggested length, a field the seller has not filled in. Deleting
    // the value over one of those would destroy the seller's own text to enforce
    // a preference, which is the opposite of the point. Pass them through; the
    // severity partition at the end of `validateThemeJson` files them as warnings.
    const unrenderable = out.errors.filter((e) => !ADVISORY_CODES.has(e.code));
    const advisory = out.errors.filter((e) => ADVISORY_CODES.has(e.code));
    if (unrenderable.length === 0)
        return out;
    if (bag && field.id in bag)
        delete bag[field.id];
    return {
        errors: advisory,
        warnings: [
            ...out.warnings,
            ...unrenderable.map((e) => ({
                ...e,
                code: `healed_${e.code}`,
                message: `${e.message} — value dropped, the section falls back to its default`,
            })),
        ],
    };
}
/** Returns errors and warnings for a single field's value against its schema. */
function validateFieldValue(field, value, basePath) {
    const errors = [];
    const warnings = [];
    const path = `${basePath}.${field.id}`;
    // Conditional fields (showIf) skip validation when their condition is false.
    // The editor enforces this in the UI; here we just allow `undefined`.
    if (value === undefined || value === null) {
        if (field.validate?.required === true) {
            errors.push({
                path,
                code: "required",
                message: `${field.id} is required`,
            });
        }
        return { errors, warnings };
    }
    switch (field.type) {
        case "text":
        case "textarea":
        case "richText": {
            if (field.i18n) {
                const parsed = localeStringSchema.safeParse(value);
                if (!parsed.success) {
                    errors.push({
                        path,
                        code: "invalid_type",
                        message: "Expected { ar, en } object",
                    });
                    break;
                }
                const { ar, en } = parsed.data;
                const both = (ar ?? "").trim().length === 0 && (en ?? "").trim().length === 0;
                // Empty bilingual fields are allowed at draft time but blocked at publish
                // by `validateForPublish`. Here we only enforce length bounds when filled.
                if (field.validate?.min !== undefined &&
                    ar &&
                    ar.length < field.validate.min &&
                    en &&
                    en.length < field.validate.min) {
                    errors.push({
                        path,
                        code: "too_short",
                        message: `Min ${field.validate.min} chars`,
                    });
                }
                if (field.validate?.max !== undefined) {
                    if (ar && ar.length > field.validate.max) {
                        errors.push({
                            path: `${path}.ar`,
                            code: "too_long",
                            message: `Max ${field.validate.max} chars`,
                        });
                    }
                    if (en && en.length > field.validate.max) {
                        errors.push({
                            path: `${path}.en`,
                            code: "too_long",
                            message: `Max ${field.validate.max} chars`,
                        });
                    }
                }
                // Empty bilingual on a `validate.required` field is a warning at
                // draft time and promoted to an error at publish time. Optional
                // bilingual fields are allowed to stay empty — they fall back to
                // SSR-rendered defaults (e.g., hero headline falls back to the
                // store name). Don't surface a warning at all for those.
                if (both && field.validate?.required === true) {
                    warnings.push({
                        path,
                        code: "bilingual_empty",
                        message: `Both locales empty for ${field.id}`,
                    });
                }
            }
            else {
                if (typeof value !== "string") {
                    errors.push({
                        path,
                        code: "invalid_type",
                        message: "Expected string",
                    });
                    break;
                }
                if (field.validate?.min !== undefined &&
                    value.length < field.validate.min) {
                    errors.push({
                        path,
                        code: "too_short",
                        message: `Min ${field.validate.min} chars`,
                    });
                }
                if (field.validate?.max !== undefined &&
                    value.length > field.validate.max) {
                    errors.push({
                        path,
                        code: "too_long",
                        message: `Max ${field.validate.max} chars`,
                    });
                }
            }
            break;
        }
        case "image": {
            const parsed = imageValueSchema.safeParse(value);
            if (!parsed.success) {
                errors.push({
                    path,
                    code: "invalid_image",
                    message: "Image must be { objectKey, url, ... }",
                });
            }
            break;
        }
        case "url": {
            // Accept any of: empty string ("no link"), `#` (anchor placeholder
            // — what sellers paste while still authoring), relative paths
            // (`/collections/foo`), absolute http(s) URLs, and the common
            // protocols `mailto:` / `tel:`. The schema's `validate.required`
            // already controls whether a URL is mandatory; this case only
            // rejects clearly malformed strings.
            if (typeof value !== "string") {
                errors.push({
                    path,
                    code: "invalid_url",
                    message: "Expected URL string",
                });
                break;
            }
            const trimmed = value.trim();
            const isAllowed = trimmed.length === 0 ||
                trimmed === "#" ||
                trimmed.startsWith("/") ||
                /^https?:\/\//i.test(trimmed) ||
                /^mailto:/i.test(trimmed) ||
                /^tel:/i.test(trimmed);
            if (!isAllowed) {
                // A scheme the storefront must not emit (`javascript:`, `data:`, …)
                // is unrenderable and gets dropped; scheme-less text is merely a link
                // that goes nowhere useful, and is kept (advisory).
                const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
                errors.push({
                    path,
                    code: hasScheme ? "invalid_url" : "unresolved_url",
                    message: hasScheme
                        ? "Expected URL, relative path, mailto:, or tel:"
                        : "Not a link the storefront can follow — kept as typed",
                });
            }
            break;
        }
        case "color": {
            // Empty string === "no override; fall back to the theme token". The
            // editor's color picker clears a value this way when the seller
            // unsets a per-section background/text override, so empty must be
            // accepted at validation time.
            if (typeof value !== "string") {
                errors.push({
                    path,
                    code: "invalid_color",
                    message: "Expected color string",
                });
            }
            break;
        }
        case "colorScheme": {
            if (value !== "light" && value !== "dark" && value !== "auto") {
                errors.push({
                    path,
                    code: "invalid_colorScheme",
                    message: "Expected light|dark|auto",
                });
            }
            break;
        }
        case "select": {
            const allowed = field.options.map((o) => o.value);
            if (typeof value !== "string" || !allowed.includes(value)) {
                errors.push({
                    path,
                    code: "invalid_option",
                    message: `Expected one of ${allowed.join(",")}`,
                });
            }
            break;
        }
        case "range": {
            if (typeof value !== "number") {
                errors.push({ path, code: "invalid_type", message: "Expected number" });
                break;
            }
            if (value < field.min || value > field.max) {
                errors.push({
                    path,
                    code: "out_of_range",
                    message: `Must be ${field.min}–${field.max}`,
                });
            }
            break;
        }
        case "checkbox": {
            if (typeof value !== "boolean") {
                errors.push({
                    path,
                    code: "invalid_type",
                    message: "Expected boolean",
                });
            }
            break;
        }
        case "datetime": {
            // Stored as a `datetime-local` string (`YYYY-MM-DDTHH:mm`). Empty is
            // allowed (no target set yet); when filled it must be a parseable date.
            if (typeof value !== "string") {
                errors.push({
                    path,
                    code: "invalid_type",
                    message: "Expected datetime string",
                });
                break;
            }
            if (value.trim().length > 0 && Number.isNaN(Date.parse(value))) {
                errors.push({
                    path,
                    code: "invalid_datetime",
                    message: "Expected a valid date-time",
                });
            }
            break;
        }
        case "productPicker":
        case "categoryPicker": {
            // Single-mode pickers store a string id; multi-mode store an array.
            if (field.type === "categoryPicker" && field.single === true) {
                if (typeof value !== "string") {
                    errors.push({
                        path,
                        code: "invalid_type",
                        message: "Expected string",
                    });
                }
                break;
            }
            if (!Array.isArray(value)) {
                errors.push({ path, code: "invalid_type", message: "Expected array" });
                break;
            }
            if (field.maxItems !== undefined && value.length > field.maxItems) {
                errors.push({
                    path,
                    code: "too_many",
                    message: `Max ${field.maxItems} items`,
                });
            }
            for (const id of value) {
                if (typeof id !== "string") {
                    errors.push({
                        path,
                        code: "invalid_id",
                        message: "Each id must be string",
                    });
                    break;
                }
            }
            break;
        }
        case "headingLevel": {
            if (![1, 2, 3, 4].includes(value)) {
                errors.push({
                    path,
                    code: "invalid_heading",
                    message: "Expected 1|2|3|4",
                });
            }
            break;
        }
        default: {
            // Exhaustiveness guard — typescript catches missing cases at compile.
            const _exhaustive = field;
            errors.push({
                path,
                code: "unknown_field_type",
                message: `Unknown field: ${JSON.stringify(_exhaustive)}`,
            });
        }
    }
    return { errors, warnings };
}
function validateSection(section, basePath, template) {
    const errors = [];
    const warnings = [];
    const schema = sectionRegistry.sections[section.type];
    if (!schema) {
        errors.push({
            path: basePath,
            code: "unknown_section",
            message: `Unknown section type: ${section.type}`,
        });
        return { errors, warnings };
    }
    if (section.hiddenText) {
        const hideableFields = new Set(schema.settings
            .filter((field) => field.type === "text" || field.type === "textarea")
            .map((field) => field.id));
        for (const [index, settingId] of section.hiddenText.entries()) {
            if (!hideableFields.has(settingId))
                errors.push({
                    path: `${basePath}.hiddenText[${index}]`,
                    code: "invalid_hidden_text",
                    message: "Hidden text must reference a declared text or textarea setting",
                });
        }
    }
    // Section type must be allowed on this template (e.g., a `hero` cannot
    // appear on the product template, a `product-details` cannot appear on
    // home). Catches programming errors and DB tampering.
    if (!isSectionAvailableOnTemplate(section.type, template)) {
        errors.push({
            path: basePath,
            code: "section_not_allowed_on_template",
            message: `Section "${section.type}" is not available on template "${template}"`,
        });
    }
    // Locked-flag must match schema. Prevents flipping `locked` via DB tampering.
    if (Boolean(schema.locked) !== Boolean(section.locked)) {
        errors.push({
            path: `${basePath}.locked`,
            code: "locked_mismatch",
            message: `Section ${section.type} locked flag must be ${Boolean(schema.locked)}`,
        });
    }
    // Critical-for-SEO sections cannot be hidden — but only the LOCKED ones.
    //
    // A locked section (header, footer, product-details, the collection's product
    // grid) cannot be deleted either, so its visibility switch is the only way to
    // strip a page of the thing that makes it that page. That stays blocked.
    //
    // An unlocked one (hero, all-products) is a different story: the seller can
    // already DELETE it from the section list, and does not need our permission
    // to. Refusing the hide while allowing the delete protected no SEO at all —
    // it just meant the seller reached the same storefront by the destructive
    // route, and lost their settings doing it.
    if (schema.criticalForSeo && schema.locked && section.visible === false) {
        errors.push({
            path: `${basePath}.visible`,
            code: "cannot_hide_critical",
            message: `Section ${section.type} is critical for SEO and cannot be hidden`,
        });
    }
    // Validate each declared field, healing any value the renderer can't use.
    for (const field of schema.settings) {
        const out = validateAndHealField(field, section.settings, `${basePath}.settings`);
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    // Validate blocks if any.
    if (section.blocks && schema.blocks) {
        for (const [i, block] of section.blocks.entries()) {
            const blockSchema = schema.blocks.find((b) => b.type === block.type);
            if (!blockSchema) {
                errors.push({
                    path: `${basePath}.blocks[${i}]`,
                    code: "unknown_block",
                    message: `Unknown block type ${block.type} in section ${section.type}`,
                });
                continue;
            }
            for (const field of blockSchema.settings) {
                const out = validateAndHealField(field, block.settings, `${basePath}.blocks[${i}].settings`);
                errors.push(...out.errors);
                warnings.push(...out.warnings);
            }
        }
    }
    return { errors, warnings };
}
/**
 * Draft-mode healing for BLOCKS — the block-level twin of the
 * `stripped_unknown_section` pass below.
 *
 * A block type can disappear from a section schema the same way a section type
 * disappears from the registry: a refactor moves it elsewhere (v2→v3 moved the
 * header's `announcement` blocks into their own `announcement-bar` section) or
 * drops it outright. Any stored theme that predates the change still carries
 * those blocks, and `validateSection` rejects them with `unknown_block` — an
 * error the SELLER cannot act on, because the editor no longer offers the
 * control that produced them. That bounces every draft save and locks the
 * seller out of their own editor.
 *
 * So in draft mode we do what we already do for unknown sections: drop the
 * block, record a warning, and let the save through. Publish mode keeps the
 * strict path — but by then the draft has already been healed on save, so a
 * healed draft publishes cleanly.
 *
 * Only strips when the section's schema DECLARES blocks and the type isn't
 * among them. A schema with no `blocks` at all is left alone: `validateSection`
 * ignores those blocks rather than erroring, so there is nothing to heal and
 * nothing to lose.
 *
 * Mutates `sections` in place (the draft-mode pass already works this way).
 */
function stripUnknownBlocks(sections, basePath, warnings) {
    for (const section of sections) {
        const schema = sectionRegistry.sections[section.type];
        if (!schema?.blocks?.length || !section.blocks)
            continue;
        const declared = new Set(schema.blocks.map((b) => b.type));
        section.blocks = section.blocks.filter((b) => {
            if (declared.has(b.type))
                return true;
            warnings.push({
                path: `${basePath}.sections`,
                code: "stripped_unknown_block",
                message: `Stripped unknown block type "${b.type}" (id="${b.id}") from section ${section.type}`,
            });
            return false;
        });
    }
}
/** The slug shape the buyer's routes can actually resolve. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,59}$/;
/**
 * Coerce a custom page's slug into something routable and unique.
 *
 * A slug is the page's URL, which makes a malformed or duplicated one the one
 * problem on a custom page that genuinely breaks the storefront — two pages
 * answering the same address, or an address the router cannot match. It is also
 * the one we can repair without guessing at intent: lowercase it, drop what
 * isn't a letter/digit/hyphen, and suffix it until it is unique.
 *
 * Falls back to the page id when nothing survives the clean-up (a slug written
 * entirely in Arabic, say — legal for a NAME, never for a URL). A page the
 * seller has to rename is recoverable; a save they cannot make is not.
 */
function healSlug(slug, pageId, taken, warnings) {
    const cleaned = (slug ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
    let candidate = SLUG_RE.test(cleaned) ? cleaned : `page-${pageId}`.toLowerCase().slice(0, 60);
    if (!SLUG_RE.test(candidate))
        candidate = "page";
    const base = candidate;
    for (let n = 2; taken.has(candidate); n += 1) {
        candidate = `${base.slice(0, 57)}-${n}`;
    }
    if (candidate !== slug) {
        warnings.push({
            path: `pages.${pageId}.slug`,
            code: "healed_slug",
            message: `Rewrote page slug "${slug}" to "${candidate}"`,
        });
    }
    return candidate;
}
/** Which section types play the header/footer roles for a given store kind. */
function chromeTypes(storeKind) {
    return storeKind === "RESTAURANT"
        ? { header: "restaurant-header", footer: "restaurant-footer" }
        : { header: "header", footer: "footer" };
}
/**
 * Draft-mode healing for CHROME ORDER — the positional twin of the
 * `inserted_missing_locked` pass.
 *
 * `header_position` / `footer_position` are the two errors a seller can do
 * least about. The editor already refuses to move the header or the footer, so
 * a draft that trips them was not built by the seller: it is legacy content, a
 * bad import, or a preset assembled before the rule existed. The seller just
 * sees every autosave fail with "the header has to stay at the very top",
 * pointing at a section they cannot drag.
 *
 * The fix the message asks for is mechanical, so do it rather than demand it:
 * move the header to the front and the footer to the back. Publish mode keeps
 * the strict check — by then the draft has been healed on save, exactly as the
 * unknown-section and missing-locked passes assume.
 *
 * Mutates `sections` in place.
 */
function reorderChrome(sections, basePath, storeKind, warnings) {
    const { header, footer } = chromeTypes(storeKind);
    const headerIdx = sections.findIndex((s) => s.type === header);
    if (headerIdx > 0) {
        const [moved] = sections.splice(headerIdx, 1);
        sections.unshift(moved);
        warnings.push({
            path: `${basePath}.sections`,
            code: "reordered_chrome",
            message: `Moved "${header}" back to the first position`,
        });
    }
    const footerIdx = sections.findIndex((s) => s.type === footer);
    if (footerIdx >= 0 && footerIdx !== sections.length - 1) {
        const [moved] = sections.splice(footerIdx, 1);
        sections.push(moved);
        warnings.push({
            path: `${basePath}.sections`,
            code: "reordered_chrome",
            message: `Moved "${footer}" back to the last position`,
        });
    }
}
function validateTemplate(template, basePath, templateName, storeKind) {
    const errors = [];
    const warnings = [];
    // Locked sections required ON THIS TEMPLATE must be present exactly once.
    // (e.g., `hero` is locked on home but doesn't exist on product/collection.)
    const presentTypes = new Set(template.sections.map((s) => s.type));
    for (const required of requiredLockedSectionTypes(templateName, storeKind)) {
        if (!presentTypes.has(required)) {
            errors.push({
                path: basePath,
                code: "missing_locked",
                message: `Locked section "${required}" must be present on template "${templateName}"`,
            });
        }
    }
    // Header must be first; footer must be last (universal rule across
    // templates). The section TYPES that play those roles differ by store kind —
    // a restaurant's masthead is `restaurant-header`, not `header` — so the rule
    // is the same and only the names it looks for change. Checking for the shop's
    // names on a restaurant reported both as missing on a theme that has neither
    // out of place.
    const chrome = chromeTypes(storeKind);
    if (template.sections[0]?.type !== chrome.header) {
        errors.push({
            path: `${basePath}.sections[0]`,
            code: "header_position",
            message: "Header must be the first section",
        });
    }
    if (template.sections.at(-1)?.type !== chrome.footer) {
        errors.push({
            path: `${basePath}.sections[last]`,
            code: "footer_position",
            message: "Footer must be the last section",
        });
    }
    // maxPerTemplate enforcement.
    const counts = {};
    for (const section of template.sections) {
        counts[section.type] = (counts[section.type] ?? 0) + 1;
    }
    for (const [type, count] of Object.entries(counts)) {
        const max = sectionRegistry.sections[type]?.maxPerTemplate;
        if (max !== undefined && count > max) {
            errors.push({
                path: basePath,
                code: "too_many_sections",
                message: `Section ${type}: ${count} instances, max ${max}`,
            });
        }
    }
    // Per-section validation. Pass the template name so misplaced sections
    // (e.g., a hero added to the product template) are caught.
    for (const [i, section] of template.sections.entries()) {
        const out = validateSection(section, `${basePath}.sections[${i}]`, templateName);
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    return { errors, warnings };
}
/**
 * Validate a custom (seller-authored) page. Custom pages don't carry
 * platform-locked sections like `hero` or `product-details`; they're
 * universal-only. The header/footer are still rendered by the buyer
 * shell so we don't require those in the page's own sections.
 */
function validateCustomPage(page, basePath) {
    const errors = [];
    const warnings = [];
    // Slug shape — lowercase letters/digits/hyphens, 1–60 chars.
    if (!/^[a-z0-9][a-z0-9-]{0,59}$/.test(page.slug)) {
        errors.push({
            path: `${basePath}.slug`,
            code: "invalid_slug",
            message: `Slug must be lowercase letters / digits / hyphens (1–60 chars). Got "${page.slug}".`,
        });
    }
    // Bilingual name — at least one locale must be filled at publish time.
    // Draft mode tolerates empty (warning only) so the editor can persist
    // the page while the seller is still authoring.
    const hasName = (page.name?.ar?.trim().length ?? 0) > 0 ||
        (page.name?.en?.trim().length ?? 0) > 0;
    if (!hasName) {
        warnings.push({
            path: `${basePath}.name`,
            code: "bilingual_empty",
            message: `Page name is empty in both locales`,
        });
    }
    // Per-section validation — only universal sections (banner, about, faq)
    // may live on a custom page. Locked / template-restricted sections
    // (hero, product-details, …) are rejected.
    for (const [i, section] of page.sections.entries()) {
        const schema = sectionRegistry.sections[section.type];
        if (!schema) {
            errors.push({
                path: `${basePath}.sections[${i}]`,
                code: "unknown_section",
                message: `Unknown section type: ${section.type}`,
            });
            continue;
        }
        if (schema.locked) {
            errors.push({
                path: `${basePath}.sections[${i}]`,
                code: "locked_section_on_custom_page",
                message: `Locked section "${section.type}" cannot be added to a custom page`,
            });
            continue;
        }
        const allowed = schema.availableOnTemplates;
        if (allowed && allowed.length > 0) {
            // Template-restricted section (e.g. hero is home-only) — reject on
            // custom pages. Universal sections leave `availableOnTemplates`
            // undefined.
            errors.push({
                path: `${basePath}.sections[${i}]`,
                code: "section_not_allowed_on_custom_page",
                message: `Section "${section.type}" is not available on custom pages`,
            });
            continue;
        }
        // Field-level validation reuses the section validator. We pass
        // `'home'` as the template arg because the per-template availability
        // is already enforced above; the field-level pass is template-blind.
        const out = validateSection(section, `${basePath}.sections[${i}]`, "home");
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    return { errors, warnings };
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Validate a `StoreThemeJson` structurally.
 *
 * - In `'draft'` mode (default), accepts content with empty bilingual fields
 *   — those are reported in `warnings` for the editor to surface as soft hints.
 * - In `'publish'` mode, empty bilingual fields are promoted to errors.
 *
 * Always returns warnings even when `ok: true`, so editor UX can show hints.
 */
/**
 * Validate a structured `productCard` selection against the product-card
 * registry: the card id must be a real card, and each provided setting must
 * satisfy that card's field schema. Mirrors the section-settings validation so
 * a bogus card id or out-of-range setting is rejected before publish — the
 * exact failure mode the ProductCardSchema registry exists to prevent
 * (docs/plans/2026-07-17-composable-themes.md §8 S2).
 */
function validateProductCardValue(productCard, basePath) {
    const errors = [];
    const warnings = [];
    const schema = getProductCardSchema(productCard.type);
    if (!schema) {
        errors.push({
            path: `${basePath}.type`,
            code: "unknown_product_card",
            message: `Unknown product-card type: ${productCard.type}`,
        });
        return { errors, warnings };
    }
    for (const field of schema.settings) {
        const out = validateAndHealField(field, productCard.settings, `${basePath}.settings`);
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    return { errors, warnings };
}
const sectionEnvelopeSchema = z.object({
    id: z.string().regex(/^[a-zA-Z0-9_-]{1,128}$/),
    type: z.string().regex(/^[a-zA-Z0-9_-]{1,128}$/),
    settings: z.record(z.string(), z.unknown()),
    hiddenText: z.array(z.string().regex(/^[a-zA-Z0-9_-]{1,128}$/)).max(128).optional(),
}).passthrough();
export function validateThemeJson(input, options = {}) {
    const mode = options.mode ?? "draft";
    const storeKind = options.storeKind ?? "DEFAULT";
    const errors = [];
    const warnings = [];
    const shapeOk = z
        .object({
        schemaVersion: z.number().int().positive(),
        settings: themeTokensSchema,
        templates: z.record(z.string(), z.object({ sections: z.array(sectionEnvelopeSchema) })),
        pages: z
            .record(z.string(), z.object({
            id: z.string().min(1),
            slug: z.string().min(1),
            name: localeStringSchema,
            sections: z.array(sectionEnvelopeSchema),
        }))
            .optional(),
    })
        .safeParse(input);
    if (!shapeOk.success) {
        return {
            ok: false,
            errors: shapeOk.error.issues.map((issue) => ({
                path: issue.path.join("."),
                code: issue.code,
                message: issue.message,
            })),
            warnings: [],
        };
    }
    const data = shapeOk.data;
    // Normalize each section's `locked` flag to its schema's value (schema is
    // authoritative). Heals themes saved while a type was locked but since
    // unlocked — without this they'd fail the `locked_mismatch` check below.
    const normalizeLocked = (sections) => {
        for (const s of sections) {
            const schema = sectionRegistry.sections[s.type];
            if (!schema)
                continue;
            if (schema.locked)
                s.locked = true;
            else if ("locked" in s)
                delete s.locked;
        }
    };
    for (const tpl of Object.values(data.templates)) {
        if (tpl)
            normalizeLocked(tpl.sections);
    }
    if (data.pages) {
        for (const pg of Object.values(data.pages)) {
            if (pg)
                normalizeLocked(pg.sections);
        }
    }
    if (data.schemaVersion > CURRENT_SCHEMA_VERSION) {
        errors.push({
            path: "schemaVersion",
            code: "schema_too_new",
            message: `schemaVersion ${data.schemaVersion} is newer than this build's ${CURRENT_SCHEMA_VERSION}. Upgrade the schemas package.`,
        });
    }
    // Theme tokens are written into a <style> element: bring each one inside the
    // renderer's allow-lists (trim, strip, drop or fall back) — see healThemeTokens.
    healThemeTokens(data.settings, warnings);
    // Validate the structured product-card selection against the registry. An
    // unknown card id is unrenderable, so drop the whole selection and let the
    // theme fall back to its default card — the same remedy the field healer
    // applies one level down, for the same reason.
    if (data.settings.productCard) {
        const out = validateProductCardValue(data.settings.productCard, "settings.productCard");
        if (out.errors.length > 0) {
            warnings.push(...out.errors.map((e) => ({
                ...e,
                code: `healed_${e.code}`,
                message: `${e.message} — selection dropped, the theme falls back to its default card`,
            })));
            delete data.settings.productCard;
        }
        warnings.push(...out.warnings);
    }
    // HEAL, DON'T REJECT.
    //
    // This pass used to run for `draft` only, on the reasoning that a section
    // which cannot be rendered should never reach a live storefront. True — but
    // rejecting the publish did not keep it off the storefront either. It kept
    // the seller's *other* twenty good changes off the storefront, left the bad
    // section exactly where it was, and handed them a message naming a section
    // type the editor has no control for. Removing the unrenderable thing
    // achieves what the rejection was reaching for, and publishes everything
    // else.
    //
    // So both modes now heal, and `validation.data` — the healed copy — is what
    // the backend persists. Every fix here is either a deletion of something that
    // cannot render, or a move that puts chrome back where the schema says it
    // goes; nothing invents content.
    {
        for (const [name, template] of Object.entries(data.templates)) {
            if (!template)
                continue;
            template.sections = template.sections.filter((s) => {
                const ok = !!sectionRegistry.sections[s.type];
                if (!ok) {
                    warnings.push({
                        path: `templates.sections`,
                        code: "stripped_unknown_section",
                        message: `Stripped unknown section type "${s.type}" (id="${s.id}")`,
                    });
                }
                return ok;
            });
            // Un-hide chrome that must stay visible, rather than refusing the save.
            // Deleting it is already impossible, so the seller cannot have meant this
            // and cannot undo it from the editor either.
            for (const section of template.sections) {
                const sectionSchema = sectionRegistry.sections[section.type];
                if (!sectionSchema?.criticalForSeo || !sectionSchema.locked)
                    continue;
                if (section.visible !== false)
                    continue;
                section.visible = true;
                warnings.push({
                    path: `templates.${name}.sections`,
                    code: "unhid_critical_section",
                    message: `Re-showed "${section.type}" — it is structural and cannot be hidden`,
                });
            }
            stripUnknownBlocks(template.sections, `templates.${name}`, warnings);
            // Auto-insert any required locked section that's missing. This keeps
            // older drafts compatible when we add a new locked section type
            // (e.g. `top-seller`) — the next save quietly heals the draft
            // instead of bouncing it with a missing_locked error.
            const presentTypes = new Set(template.sections.map((s) => s.type));
            for (const requiredType of requiredLockedSectionTypes(name, storeKind)) {
                if (presentTypes.has(requiredType))
                    continue;
                const stub = {
                    id: `${requiredType}-1`,
                    type: requiredType,
                    locked: true,
                    visible: true,
                    settings: {},
                };
                // Insert before the footer if footer is present (footer is always
                // last by convention); otherwise append.
                const footerIdx = template.sections.findIndex((s) => s.type === "footer");
                if (footerIdx >= 0) {
                    template.sections.splice(footerIdx, 0, stub);
                }
                else {
                    template.sections.push(stub);
                }
                warnings.push({
                    path: `templates.${name}.sections`,
                    code: "inserted_missing_locked",
                    message: `Auto-inserted missing locked section "${requiredType}"`,
                });
            }
            reorderChrome(template.sections, `templates.${name}`, storeKind === "RESTAURANT" && name === "home" ? "RESTAURANT" : "DEFAULT", warnings);
        }
        if (data.pages) {
            const takenSlugs = new Set();
            for (const page of Object.values(data.pages)) {
                if (!page)
                    continue;
                page.sections = page.sections.filter((s) => {
                    const ok = !!sectionRegistry.sections[s.type];
                    if (!ok) {
                        warnings.push({
                            path: `pages.${page.id}.sections`,
                            code: "stripped_unknown_section",
                            message: `Stripped unknown section type "${s.type}" (id="${s.id}")`,
                        });
                    }
                    return ok;
                });
                stripUnknownBlocks(page.sections, `pages.${page.id}`, warnings);
                // A slug is the page's URL, so a malformed or duplicated one is the one
                // thing here that genuinely breaks routing — and also the one thing we
                // can repair without guessing at intent. Derive a legal, unique slug
                // rather than refusing the save: an unreachable page the seller can
                // rename beats no save at all.
                page.slug = healSlug(page.slug, page.id, takenSlugs, warnings);
                takenSlugs.add(page.slug);
            }
        }
    }
    for (const [name, template] of Object.entries(data.templates)) {
        if (!template)
            continue;
        // Only HOME is a restaurant template.
        //
        // A restaurant renders exactly one page — its menu — but the stored theme
        // still carries `product` and `collection` templates, backfilled for every
        // store by `ensureAllTemplatesPresent` and never rendered here. Those stay
        // shop-shaped, so validating them against the restaurant's locked sections
        // would fail a theme for the contents of two pages nobody will ever see.
        const templateKind = storeKind === "RESTAURANT" && name === "home" ? "RESTAURANT" : "DEFAULT";
        const out = validateTemplate(template, `templates.${name}`, name, templateKind);
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    // Custom pages — same per-section validation as templates, plus a few
    // extra rules: cap, unique slugs, only universal sections.
    if (data.pages) {
        const pageEntries = Object.entries(data.pages);
        if (pageEntries.length > MAX_CUSTOM_PAGES) {
            errors.push({
                path: "pages",
                code: "too_many_pages",
                message: `At most ${MAX_CUSTOM_PAGES} custom pages allowed; found ${pageEntries.length}`,
            });
        }
        const seenSlugs = new Set();
        for (const [pageId, page] of pageEntries) {
            if (!page)
                continue;
            const out = validateCustomPage(page, `pages.${pageId}`);
            errors.push(...out.errors);
            warnings.push(...out.warnings);
            if (page.slug) {
                if (seenSlugs.has(page.slug)) {
                    errors.push({
                        path: `pages.${pageId}.slug`,
                        code: "duplicate_slug",
                        message: `Slug "${page.slug}" is used by another page`,
                    });
                }
                seenSlugs.add(page.slug);
            }
        }
    }
    if (mode === "publish") {
        // A publishable theme MUST have a home template with content. An empty
        // `templates: {}` trivially satisfies the record shape check above, so
        // without this guard a wiped theme (e.g. a hand-edited JSON mistake in the
        // admin editor) could be published and render blank for every store that
        // applies it. See docs/plans/2026-07-17-composable-themes.md §8 (S1/S7).
        //
        // Note this is a check on the SHAPE of the theme, not on how full it is: a
        // home template whose sections are all still empty publishes fine. The
        // storefront drops each empty section and renders what is left, which is
        // what a seller who publishes early actually wants to happen.
        const home = data.templates.home;
        if (!home || home.sections.length === 0) {
            errors.push({
                path: "templates.home",
                code: "missing_home_template",
                message: "A publishable theme must have a home template with at least one section.",
            });
        }
    }
    // Publish used to be where empty content became fatal: `bilingual_empty`
    // warnings were promoted to errors, so one unfilled required field anywhere
    // in the theme — including on a page the seller was not even looking at —
    // refused the publish. That promotion is gone. Publish is now strict about
    // exactly what draft is strict about (structure and renderability) and
    // forgiving about exactly what draft forgives (content the seller has not
    // written yet), which is the distinction sellers can actually reason about.
    const { blocking, advisory } = partitionBySeverity(errors);
    const allWarnings = [...warnings, ...advisory];
    if (blocking.length > 0) {
        return { ok: false, errors: blocking, warnings: allWarnings };
    }
    return { ok: true, data, warnings: allWarnings };
}
/** Validate a single section instance. Used by the editor for live feedback. */
export function validateSectionInstance(section, path = "section", template = "home") {
    return validateSection(section, path, template);
}
//# sourceMappingURL=validate.js.map